import { useEffect, useMemo, useState, useCallback } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import {
  Box,
  CircularProgress,
  Button,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { useTranslation } from "react-i18next";
import { getDevices, undefineDevice } from "../../api/deviceAPI";
import { getNode } from "../../api/nodeAPI";
import { renderOnline, renderOffline } from "../../components/StatusChip";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useInterval } from "../../hooks/useInterval";
import { getCurrentTenant } from "../../config/tenantConfig";
import DeviceControls from "./DeviceControls";
import {
  createCompactColumn,
  createStatusColumn,
} from "../../utils/dataGridColumnUtils";

export const getColumns = (t, isRcu, onUndefine) => {
  const columns = [
    createStatusColumn(
      "status",
      t("devices.dataGrid.status"),
      (params) =>
        params.value === "Online"
          ? renderOnline(params.value, t)
          : renderOffline(params.value, t),
      {},
      t,
    ),
    createCompactColumn("serialDevice", t("devices.dataGrid.serialNumber")),
    createCompactColumn("uniqueId1", t("devices.dataGrid.hwCode")),
    createCompactColumn("nameDevice", t("devices.dataGrid.deviceName")),
    createCompactColumn(
      "nodeName",
      t("devices.dataGrid.nodeName") || "Node Name",
    ),
    createCompactColumn("type", t("devices.dataGrid.deviceType")),
  ];

  // RCU-only: per-row "Remove definition" action
  if (isRcu) {
    columns.push({
      field: "undefine",
      headerName: "",
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Tooltip
          title={t("devices.actions.undefine") || "Tanımı Kaldır"}
        >
          <IconButton
            size="small"
            color="error"
            onClick={(event) => {
              event.stopPropagation();
              onUndefine(params.row);
            }}
          >
            <LinkOffIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    });
  }

  return columns;
};

export default function DevicesDataGrid() {
  const { t } = useTranslation();
  const isRcu = getCurrentTenant().projectCode === "RCU";
  const [devices, setDevices] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  // Undefine (remove definition) state — RCU only
  const [undefineTarget, setUndefineTarget] = useState(null);
  const [undefineSerialInput, setUndefineSerialInput] = useState("");
  const [isUndefining, setIsUndefining] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Stable callbacks using useCallback
  const handleDevicesSuccess = useCallback((response) => {
    setDevices(response?.data || response || []);
  }, []);

  const handleNodesSuccess = useCallback((result) => {
    setNodes(result || []);
  }, []);

  const {
    loading,
    error,
    execute: executeDevices,
  } = useApiRequest({
    onSuccess: handleDevicesSuccess,
  });

  const { execute: executeNodes } = useApiRequest({
    showErrorInConsole: true,
    onSuccess: handleNodesSuccess,
  });

  const loadData = useCallback(async () => {
    try {
      await executeDevices(getDevices, {
        errorMessage: "Failed to fetch devices",
      });
      await executeNodes(getNode);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Error loading data:", error);
    }
  }, [executeDevices, executeNodes]);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh every 30 seconds with manual controls
  const { pause, resume, reset } = useInterval(loadData, 30000, {
    immediate: false,
    enabled: true,
  });

  // Handle pause/resume
  const handlePauseResume = useCallback(() => {
    if (isPaused) {
      resume();
      setIsPaused(false);
    } else {
      pause();
      setIsPaused(true);
    }
  }, [isPaused, pause, resume]);

  // Handle manual refresh (resets the interval timer)
  const handleManualRefresh = useCallback(() => {
    loadData();
    reset(); // Reset interval timer
  }, [loadData, reset]);

  const rows = useMemo(
    () =>
      devices.map((device) => ({
        id: device.idDevice,
        status: device.isOnline === 1 ? "Online" : "Offline",
        serialDevice: device.serialDevice,
        nameDevice: device.nameDevice,
        nodeId: device.node_idNode,
        nodeName:
          nodes.find((n) => n.idNode === device.node_idNode)?.name ||
          device.node_idNode ||
          "",
        type: device.type,
        uniqueId1: device.uniqueId1 || "",
      })),
    [devices, nodes],
  );

  const handleRowClick = (params) => {
    if (params.row.nodeId && String(params.row.nodeId).trim() !== "") {
      window.open(`/vending-machines/${params.row.nodeId}`, "_blank");
    }
  };

  const handleSnackbarClose = () =>
    setSnackbar((s) => ({ ...s, open: false }));

  // Open confirm dialog for removing a device definition
  const handleUndefineClick = useCallback((row) => {
    setUndefineSerialInput("");
    setUndefineTarget(row);
  }, []);

  const handleCloseUndefineDialog = () => {
    setUndefineTarget(null);
    setUndefineSerialInput("");
  };

  const handleConfirmUndefine = async () => {
    if (isUndefining || !undefineTarget) return;
    // Require the user to type the exact serial number before removing
    if (
      undefineSerialInput.trim() !==
      (undefineTarget.serialDevice || "").trim()
    ) {
      setSnackbar({
        open: true,
        message:
          t("devices.dialogs.undefineDevice.serialMismatch") ||
          "Seri numarası eşleşmiyor.",
        severity: "error",
      });
      return;
    }
    // UnDefineDevice expects the full Device object coming from the service
    const fullDevice = devices.find(
      (d) => d.idDevice === undefineTarget.id,
    );
    if (!fullDevice) {
      setSnackbar({
        open: true,
        message:
          t("devices.messages.errorUndefiningDevice") ||
          "Terminal tanımı kaldırılamadı",
        severity: "error",
      });
      setUndefineTarget(null);
      return;
    }
    try {
      setIsUndefining(true);
      await undefineDevice(fullDevice);
      setSnackbar({
        open: true,
        message:
          t("devices.messages.deviceUndefined") || "Terminal tanımı kaldırıldı",
        severity: "success",
      });
      setUndefineTarget(null);
      await loadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error.message ||
          t("devices.messages.errorUndefiningDevice") ||
          "Terminal tanımı kaldırılamadı",
        severity: "error",
      });
    } finally {
      setIsUndefining(false);
    }
  };

  // RCU: left-align every header + cell.
  const baseColumns = getColumns(t, isRcu, handleUndefineClick);
  const columns = isRcu
    ? baseColumns.map((c) => ({ ...c, align: "left", headerAlign: "left" }))
    : baseColumns;

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
      }}
    >
      {/* Control Panel */}
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          {/* Add Device Button - Left side on desktop (RCU only) */}
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <DeviceControls onDevicesUpdate={loadData} />
          </Box>

          {/* Statistics Cards */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{ flex: 1 }}
          >
            {/* Add Device Button - Top on mobile (RCU only) */}
            <Box sx={{ display: { xs: "block", md: "none" }, width: "100%" }}>
              <DeviceControls onDevicesUpdate={loadData} />
            </Box>

            <Stack direction="column" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: "rgba(25, 118, 210, 0.1)",
                  border: 1.5,
                  borderColor: "primary.main",
                  color: "primary.main",
                  minWidth: 60,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {rows.length}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                fontSize="0.75rem"
                color="text.secondary"
              >
                {t("devices.total") || "Terminal"}
              </Typography>
            </Stack>

            <Stack direction="column" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: "rgba(46, 125, 50, 0.1)",
                  border: 1.5,
                  borderColor: "success.main",
                  color: "success.main",
                  minWidth: 60,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {rows.filter((r) => r.status === "Online").length}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                fontSize="0.75rem"
                color="text.secondary"
              >
                {t("devices.online") || "Online"}
              </Typography>
            </Stack>

            <Stack direction="column" spacing={0.5} alignItems="center">
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: "rgba(211, 47, 47, 0.1)",
                  border: 1.5,
                  borderColor: "error.main",
                  color: "error.main",
                  minWidth: 60,
                  textAlign: "center",
                }}
              >
                <Typography variant="h6" fontWeight="bold">
                  {rows.filter((r) => r.status === "Offline").length}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                fontSize="0.75rem"
                color="text.secondary"
              >
                {t("devices.offline") || "Offline"}
              </Typography>
            </Stack>
          </Stack>

          {/* Controls */}
          <Stack
            direction="column"
            spacing={1}
            alignItems="center"
            sx={{ width: { xs: "100%", md: "auto" } }}
          >
            {lastRefresh && (
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
              >
                {t("lastRefresh") || "Last refresh"}:{" "}
                {lastRefresh.toLocaleTimeString()}
              </Typography>
            )}

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 0 }}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <Button
                onClick={handlePauseResume}
                startIcon={isPaused ? <PlayArrowIcon /> : <PauseIcon />}
                color={isPaused ? "success" : "warning"}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  borderRadius: { xs: 1, sm: 0 },
                  borderTopLeftRadius: { sm: 1 },
                  borderBottomLeftRadius: { sm: 1 },
                  borderRight: { sm: "none" },
                }}
              >
                {isPaused ? t("resume") || "Resume" : t("pause") || "Pause"}
              </Button>
              <Button
                onClick={handleManualRefresh}
                startIcon={<RefreshIcon />}
                disabled={loading}
                variant="outlined"
                size="small"
                fullWidth
                sx={{
                  borderRadius: { xs: 1, sm: 0 },
                  borderTopRightRadius: { sm: 1 },
                  borderBottomRightRadius: { sm: 1 },
                }}
              >
                {t("refresh") || "Refresh"}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Data Grid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: "100%",
          position: "relative",
        }}
      >
        {(loading || rows.length === 0) && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              zIndex: 1,
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box sx={{ my: 2, color: "red", textAlign: "center" }}>{error}</Box>
        )}
        <DataGrid
          rows={rows}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
            sorting: {
              sortModel: [{ field: "status", sort: "desc" }],
            },
          }}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick
        />
      </Box>

      {/* Undefine confirm dialog (RCU only) */}
      <Dialog
        open={Boolean(undefineTarget)}
        onClose={handleCloseUndefineDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t("devices.dialogs.undefineDevice.title") || "Tanımı Kaldır"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("devices.dialogs.undefineDevice.confirm") ||
              "Bu terminalin tanımını kaldırmak istediğinizden emin misiniz?"}
          </DialogContentText>
          <DialogContentText sx={{ mb: 1 }}>
            {t("devices.dialogs.undefineDevice.enterSerialToConfirm") ||
              "Kaldırmak için seri numarasını giriniz:"}{" "}
            <Box
              component="span"
              sx={{
                color: "text.primary",
                fontWeight: 700,
                fontFamily:
                  'Monaco, Menlo, Consolas, "Liberation Mono", "Courier New", monospace',
              }}
            >
              {undefineTarget?.serialDevice}
            </Box>
          </DialogContentText>
          <TextField
            fullWidth
            autoFocus
            placeholder={t("devices.dataGrid.serialNumber") || "Seri No"}
            value={undefineSerialInput}
            onChange={(e) => setUndefineSerialInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseUndefineDialog}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmUndefine}
            color="error"
            variant="contained"
            disabled={
              isUndefining ||
              undefineSerialInput.trim() !==
                (undefineTarget?.serialDevice || "").trim()
            }
            sx={{ borderRadius: 2, minWidth: 120 }}
          >
            {isUndefining ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} thickness={5} color="inherit" />
                <span>{t("common.deleting") || "Deleting..."}</span>
              </Stack>
            ) : (
              t("devices.actions.undefine") || "Tanımı Kaldır"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === "error" ? 3000 : 6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          variant="filled"
          elevation={6}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
